/**
 * Canonical alphanumeric CNPJ helpers (Receita Federal, Jul/2026).
 * Positions 1–12: A–Z or 0–9. Positions 13–14: numeric check digits.
 * Numeric CNPJs remain valid (digit values are unchanged).
 */

const CNPJ_CLEAN_REGEX = /^[A-Z0-9]{12}\d{2}$/
const DV1_WEIGHTS = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
const DV2_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]

function charValue (ch) {
  return ch.charCodeAt(0) - 48
}

function computeDv (body, weights) {
  let sum = 0
  for (let i = 0; i < body.length; i++) {
    sum += charValue(body[i]) * weights[i]
  }
  const remainder = sum % 11
  if (remainder === 0 || remainder === 1) {
    return 0
  }
  return 11 - remainder
}

export function normalizeDocument (value) {
  if (value === null || value === undefined) {
    return ''
  }
  return String(value).replace(/[./\-\s]/g, '').toUpperCase()
}

export function isValidCnpj (value) {
  const normalized = normalizeDocument(value)
  if (!CNPJ_CLEAN_REGEX.test(normalized)) {
    return false
  }
  const base = normalized.slice(0, 12)
  const dv1 = String(computeDv(base, DV1_WEIGHTS))
  const dv2 = String(computeDv(base + dv1, DV2_WEIGHTS))
  return normalized.slice(12) === dv1 + dv2
}

export function formatCnpj (value) {
  const normalized = normalizeDocument(value)
  if (!CNPJ_CLEAN_REGEX.test(normalized)) {
    return ''
  }
  return (
    normalized.slice(0, 2) + '.' +
    normalized.slice(2, 5) + '.' +
    normalized.slice(5, 8) + '/' +
    normalized.slice(8, 12) + '-' +
    normalized.slice(12, 14)
  )
}

export function getDocumentType (value) {
  const normalized = normalizeDocument(value)
  if (/^\d{11}$/.test(normalized)) {
    return 'cpf'
  }
  if (CNPJ_CLEAN_REGEX.test(normalized)) {
    return 'cnpj'
  }
  return null
}

export function getCnpjRoot (value) {
  const normalized = normalizeDocument(value)
  if (!CNPJ_CLEAN_REGEX.test(normalized)) {
    return ''
  }
  return normalized.slice(0, 8)
}

export function getEstablishmentOrder (value) {
  const normalized = normalizeDocument(value)
  if (!CNPJ_CLEAN_REGEX.test(normalized)) {
    return ''
  }
  return normalized.slice(8, 12)
}

/**
 * Estrutural de proposito: avalia a ordem do estabelecimento mesmo quando o DV
 * nao confere. Um CNPJ de filial com DV invalido na base precisa continuar
 * bloqueado (a DCTFWeb so e transmitida pela matriz); exigir DV valido aqui
 * faria dado sujo passar como matriz.
 */
export function isCnpjFilial (value) {
  const order = getEstablishmentOrder(value)
  if (!order) {
    return false
  }
  return order !== '0001'
}

export function formatCnpjRoot (value) {
  const normalized = normalizeDocument(value)
  const root = CNPJ_CLEAN_REGEX.test(normalized)
    ? normalized.slice(0, 8)
    : normalized
  if (!/^[A-Z0-9]{8}$/.test(root)) {
    return ''
  }
  return root.slice(0, 2) + '.' + root.slice(2, 5) + '.' + root.slice(5, 8)
}
